import { Avatar, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import { clearKeychain, unlock } from '@plug-core/handlers/walletActions/wallet'
import plug from '@assets/icons/plug-logo.png'
import Touchable from '@components/Touchable'
import { useTranslation } from 'react-i18next'
import { usePendingRequests } from '@plug-core/state'
import { logger } from '@plug-core/logger'
import Prompt from '@components/Prompt'
import { HiMiniExclamationTriangle } from 'react-icons/hi2'
import { initializeMessenger } from '@core/messengers'

const backgroundMessenger = initializeMessenger({ connect: 'background' })

export const WalletUnlock = () => {
  const { pendingRequests, removePendingRequest } = usePendingRequests()

  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [showWalletResetConfirmation, setShowWalletResetConfirmation] = useState(false)

  const { palette } = useTheme()
  const { t } = useTranslation()

  const performUnlock = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      setIsLoading(true)
      const result = await unlock(password)
      if (result === false) {
        setError(true)
        return
      }

      const unlockRequests = pendingRequests.filter(({ method }) => method === 'unlock')

      if (unlockRequests.length) {
        unlockRequests.forEach((req) => {
          backgroundMessenger.send(`message:${req.id}`, true)
          removePendingRequest(req.id)
        })

        const hasOtherRequests = pendingRequests.some(({ method }) => method !== 'unlock')

        if (!hasOtherRequests && chrome?.windows) {
          try {
            const currentWindow = await chrome.windows.getCurrent()
            if (currentWindow.id) {
              chrome.windows.remove(currentWindow.id)
            }
          } catch (error) {
            logger.debug('Failed to close window:', error)
          }
        }
      }
    } catch (error) {
      logger.debug('Failed to unlock wallet', { originalError: error })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack
      display="flex"
      justifyContent="center"
      alignContent="center"
      width="100%"
      height="100%"
      spacing={4}
    >
      <Stack spacing={0.5}>
        <img src={plug} width="120px" style={{ alignSelf: 'center' }} />
        <Typography fontWeight="bold" textAlign="center" variant="h5">
          Welcome back!
        </Typography>
        <Typography textAlign="center" variant="body2" color="text.secondary">
          Plug into the internet computer
        </Typography>
      </Stack>

      <form onSubmit={performUnlock}>
        <Stack spacing={2} px={2}>
          <RainbowTextField
            type="password"
            placeholder="Enter password"
            fullWidth
            disabled={isLoading}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
            autoComplete="off"
            autoFocus
          />
          <Button
            type="submit"
            variant="rainbow"
            fullWidth
            disabled={password.length === 0 || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : t('unlock.title')}
          </Button>
          <Touchable onClick={() => setShowWalletResetConfirmation(true)}>
            <Typography
              color={(theme) => theme.palette.rainbow.blue}
              textAlign="center"
              fontWeight="bold"
              fontSize={14}
            >
              {t('unlock.moreOptions')}
            </Typography>
          </Touchable>
        </Stack>
      </form>

      <Prompt show={!!showWalletResetConfirmation}>
        <Stack alignItems="center">
          <Avatar sx={{ bgcolor: 'background.default', width: 50, height: 50 }}>
            <HiMiniExclamationTriangle color={palette.rainbow.yellow} size={30} />
          </Avatar>
          <Stack alignItems="center">
            <Typography variant="h6" mt={0.2}>
              {t('unlock.logoutWarning.title')}
            </Typography>
            <Typography textAlign="center" mt={1} mb={2} color="text.light">
              {t('unlock.logoutWarning.description')}
            </Typography>
          </Stack>

          <Stack width="100%" spacing={1}>
            <Button fullWidth onClick={clearKeychain} variant="destroy">
              {t('unlock.logoutWarning.confirm')}
            </Button>
            <Button
              fullWidth
              onClick={() => setShowWalletResetConfirmation(false)}
              variant="outlined"
            >
              {t('unlock.logoutWarning.cancel')}
            </Button>
          </Stack>
        </Stack>
      </Prompt>
    </Stack>
  )
}
